/**
 * Экран результата.
 *
 * Показывает рост относительно baseline, слабые зоны, XP и не более трёх
 * рекомендаций (ТЗ §7.7, §16.3 шаг 9).
 *
 * Порядок: факт завершения, измеренное, один следующий шаг — и уже потом
 * разбор (ТЗ UI-49). Сравнение показывается только при существующей базе:
 * стрелка роста без измеренного «раньше» — выдумка, а не измерение.
 *
 * Основное действие ровно одно. Наступил срок отложенной проверки — им
 * становится она; в остальное время — ещё одно занятие. Две залитые кнопки
 * рядом означают, что человек не знает, куда нажать.
 */

import type { Translate } from '@type2x/i18n';
import {
  Button,
  Card,
  EmptyState,
  HeatMap,
  MetricsBar,
  ResultMark,
  type MetricsBarProps,
} from '@type2x/ui';
import type { AchievementId } from '@type2x/feature-personal-workspace';
import type { SessionOutcomeSummary } from '@type2x/feature-progress';
import { LockedHint, ModeMenu, type ModeMenuProps } from './ModeMenu.js';
import type { PractisedMode } from '../state/app-state.js';
import { repeatAction, trackAction } from '../state/repeat.js';

export interface ResultScreenProps extends ModeMenuProps {
  readonly summary: SessionOutcomeSummary;
  /** Чем занимались: «ещё раз» повторяет это, а не уводит в основную колею. */
  readonly practised: PractisedMode;
  /** Ведёт ли основная колея в «Основы»: кнопка обязана называть, куда ведёт. */
  readonly resumesToFundamentals: boolean;
  readonly onAgain: () => void;
  readonly onSaveProgress: () => void;
  readonly onStartControlTest: () => void;
  readonly onOpenReport: () => void;
  /** Наступил ли срок отложенной проверки сохранения навыка. */
  readonly retentionDue: boolean;
  /** Показывать ли XP и уровень (ТЗ FR-GM-08). */
  readonly gameElements: boolean;
  /** Рубежи, взятые этим занятием; пустой список — ни одного. */
  readonly earned: readonly AchievementId[];
}

function round(value: number): number {
  return Math.round(value);
}

/** Подписи метрик: их набор один и тот же на всех экранах с метриками. */
function metricLabels(t: Translate): MetricsBarProps['labels'] {
  return {
    wpm: t('metrics.wpm'),
    cpm: t('metrics.cpm'),
    accuracy: t('metrics.accuracy'),
    rhythm: t('metrics.rhythm'),
    corrections: t('metrics.corrections'),
  };
}

export function ResultScreen(props: ResultScreenProps): React.JSX.Element {
  const { t, summary, retentionDue } = props;
  const { growth } = summary;

  return (
    <div className="t2x-result">
      <Card title={t('result.title')}>
        {/* Факт завершения — первое, что человек видит: знак и слово. Знак
            спокойный: занятие не заканчивается успехом или неуспехом, оно
            просто заканчивается (ТЗ UI-49, UI-69). */}
        <p className="t2x-result__done">
          <ResultMark />
          <span>{t('result.done')}</span>
        </p>

        <MetricsBar metrics={summary.metrics} labels={metricLabels(t)} />

        {growth !== null && <GrowthList t={t} growth={growth} />}

        {/* XP и уровень — игровые элементы: выключены, значит не показываются.
            Сами начисления при этом идут своим чередом: выключатель убирает
            показ, а не отменяет заработанное (ТЗ FR-GM-08). */}
        {props.gameElements && (
          <p className="t2x-xp">
            {t('result.xpEarned', {
              amount: summary.xpAwarded.reduce((sum, item) => sum + item.amount, 0),
            })}
            {' · '}
            {t('result.level', { level: summary.level })}
          </p>
        )}

        {/* Взятый рубеж называется в тот момент, когда он взят: на экране
            достижений он уже просто стоит в списке, и человек узнаёт о нём,
            только если сходит туда сам (ТЗ FR-GM-07, UX-09). Рубеж —
            игровой элемент и уходит вместе с ними: начисление при этом идёт
            своим чередом (ТЗ FR-EN-76). */}
        {props.gameElements && props.earned.length > 0 && (
          <p className="t2x-result__earned">
            {t('result.achievementEarned', {
              names: props.earned.map((id) => t(`achievements.name.${id}`)).join(', '),
            })}
          </p>
        )}

        <section>
          <h3>{t('result.nextStep')}</h3>
          <ul className="t2x-recommendations">
            {summary.recommendations.map((recommendation) => (
              <li key={recommendation.kind}>{t(`recommendation.${recommendation.kind}`)}</li>
            ))}
          </ul>
        </section>

        <p className="t2x-muted">
          {retentionDue ? t('result.retentionDue') : t('result.verifiedHint')}
        </p>

        <ResultActions {...props} />

        {/* Разбор — после действия. Пустой список ошибок говорит только про
            это занятие: «техника идеальна» приложение проверить не может
            (ТЗ UI-50). */}
        {summary.weakZones.length > 0 ? (
          <HeatMap
            caption={t('result.weakUnits')}
            cells={summary.weakZones.map((zone) => ({
              label: zone.content,
              intensity: zone.severity,
            }))}
          />
        ) : (
          <EmptyState title={t('result.noWeakUnitsTitle')} body={t('result.noWeakUnits')} />
        )}
      </Card>
    </div>
  );
}

/** Рост относительно baseline: три числа, которые пользователь сравнивает. */
function GrowthList({
  t,
  growth,
}: {
  readonly t: Translate;
  readonly growth: NonNullable<SessionOutcomeSummary['growth']>;
}): React.JSX.Element {
  return (
    <dl className="t2x-growth">
      <div>
        <dt>{t('result.baseline')}</dt>
        <dd>{round(growth.baselineCpm)} CPM</dd>
      </div>
      <div>
        <dt>{t('result.current')}</dt>
        <dd>{round(growth.currentCpm)} CPM</dd>
      </div>
      <div>
        <dt>{t('result.growth')}</dt>
        <dd>
          {growth.deltaCpm >= 0 ? '+' : ''}
          {round(growth.deltaCpm)} CPM
        </dd>
      </div>
    </dl>
  );
}

/**
 * Кнопки перехода с экрана результата.
 *
 * Каталог режимов — половина разметки экрана, и до этапа ZZ он стоял
 * развёрнутым: четырнадцать кнопок в четырёх группах между результатом и низом
 * страницы. Сразу после занятия человек отвечает на два вопроса — «как вышло»
 * и «что дальше», — и оба ответа выше каталога. Поэтому каталог раскрывается по
 * требованию, тем же приёмом, что подробности задания в `DrillLayout`: ничего
 * не убрано, убрана обязанность это пролистать.
 *
 * Что осталось на виду, решалось по тому же правилу: продолжение занятия,
 * контрольный тест, отчёт и сохранение прогресса — ответы на вопрос «что
 * дальше»; выбор другого режима — уже отдельное решение.
 *
 * Список режимов общий с главной (`ModeMenu`): две копии разошлись бы при
 * первом же новом режиме.
 */
function ResultActions(props: ResultScreenProps): React.JSX.Element {
  const { t, onSaveProgress, onStartControlTest, onOpenReport, retentionDue } = props;

  /*
   * Главное действие повторяет то, чем занимались, и называет его. Раньше оно
   * называлось «ещё одна тренировка» и вело в основную колею независимо от
   * режима: закончивший «Предложения» получал упражнение на три буквы.
   *
   * Возврат в колею остаётся рядом отдельной кнопкой — но только когда он
   * ведёт не туда же, куда повтор.
   */
  const context = {
    ...props,
    latinReady: props.latin.ready,
    layoutSwitchReady: props.layoutSwitch.ready,
  };
  const again = repeatAction(context, props.practised);
  const track = trackAction(context, props.practised);

  /*
   * Экран, советующий перерыв, не зовёт в занятие залитой кнопкой.
   *
   * ТЗ UI-49 просит один главный следующий шаг. Когда ритм рваный, этот шаг —
   * «остановиться», и кнопки у него нет: заливку в этом случае не получает
   * никто. Кнопки остаются на месте — запрета нет, — но экран перестаёт
   * тянуть человека туда, откуда сам же советует уйти.
   */
  const resting = props.summary.recommendations.some((item) => item.kind === 'rest');

  return (
    <>
      <div className="t2x-actions">
        <Button variant={resting || retentionDue ? 'secondary' : 'primary'} onClick={again.run}>
          {again.title}
        </Button>
        {track !== null && (
          <Button variant="secondary" onClick={track.run}>
            {track.title}
          </Button>
        )}
        <Button
          variant={!resting && retentionDue ? 'primary' : 'secondary'}
          onClick={onStartControlTest}
        >
          {t(retentionDue ? 'control.startRetention' : 'control.start')}
        </Button>
        <Button variant="ghost" onClick={onOpenReport}>
          {t('report.open')}
        </Button>
        <Button variant="ghost" onClick={onSaveProgress}>
          {t('account.title')}
        </Button>
      </div>

      <details className="t2x-result__more">
        <summary>{t('result.otherModes')}</summary>
        <div className="t2x-result__modes">
          <ModeMenu {...props} />
          <LockedHint {...props} />
        </div>
      </details>
    </>
  );
}
